import * as React from 'react'
import { Link, To } from 'react-router-dom'

import { TIMEFRAME } from '../../constants'
import { BoxRounding } from '../box'
import { ButtonHTMLProps, ButtonProps, ButtonSizeType } from '../button'
import { DropdownSeparator } from '../dropdown'
import { Icon } from '../icon'
import { SelectOption, SelectWithTooltip } from '../select'
import { SelectItem } from '../select/types'

export interface DateRangeBaseOption extends SelectItem<string> {
  timeframe?: TIMEFRAME
  href?: To
  withReload?: boolean
}

interface Props<DateRange> {
  chartIndex: number
  dateRangeGroups: Array<{ label?: string; dateRanges: DateRange[] }>
  selectedDateRange: string | null
  onDateRangeSelect: (value: DateRange) => void
  rounding: keyof typeof BoxRounding
  size?: ButtonSizeType
}

const DATERANGE_TOOLTIP = 'Range'

export function DateRangeSelect<DateRange extends DateRangeBaseOption>({
  size,
  chartIndex,
  dateRangeGroups,
  selectedDateRange,
  onDateRangeSelect,
  rounding,
}: Props<DateRange>) {
  const flatDateRanges = React.useMemo(() => dateRangeGroups.flatMap(({ dateRanges }) => dateRanges), [dateRangeGroups])

  const buttonProps = React.useMemo<ButtonProps & ButtonHTMLProps>(
    () => ({
      size,
      rounding,
      className: '',
      rightContent: undefined,
      appearance: 'square',
      theme: 'chip',
    }),
    [size, rounding]
  )

  const selectWithTooltipListProps = React.useMemo(() => ({ rounding }), [rounding])

  return (
    <SelectWithTooltip
      {...buttonProps}
      aria-label={DATERANGE_TOOLTIP}
      onChange={onDateRangeSelect}
      value={selectedDateRange ?? undefined}
      items={flatDateRanges}
      data-testid={`chart-${chartIndex}-settings-date-range-select`}
      buttonContent={<Icon name="calendar" />}
      tooltipLabel={DATERANGE_TOOLTIP}
      active={!!selectedDateRange}
      hasPopoverViewportHeightLimit
      isNativeMobileSelect={false}
      hasPopoverButtonWidthLimit={false}
      listProps={selectWithTooltipListProps}
    >
      {dateRangeGroups.flatMap(({ label, dateRanges }, index) => [
        <DropdownSeparator key={`${label}-${index}`}>{label}</DropdownSeparator>,
        ...dateRanges.map(({ href, value, label, withReload }) => {
          let linkProps = {}
          if (href) {
            linkProps = {
              as: Link,
              to: href,
              reloadDocument: withReload ?? !FinvizSettings.hasUserPremium,
            }
          }
          return (
            <SelectOption
              {...linkProps}
              key={value}
              data-testid={`chart-${chartIndex}-date-range-${value}`}
              className="min-w-32"
              rounding={rounding}
            >
              {label}
            </SelectOption>
          )
        }),
      ])}
    </SelectWithTooltip>
  )
}
